(() => {
  'use strict';
  if (customElements.get('catharsis-play')) return;
  const playIcon = '<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 11 7-11 7V5Z" fill="currentColor"/></svg>';
  const pauseIcon = '<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14M16 5v14" fill="none" stroke="currentColor" stroke-width="3"/></svg>';
  class CatharsisPlay extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector('.reels-dialog');
      this.scroller = this.querySelector('[data-reels-scroller]');
      this.slides = [...this.querySelectorAll('[data-reel-slide]')];
      this.videos = this.slides.map(slide => slide.querySelector('video'));
      this.previewRail = this.querySelector('[data-preview-rail]');
      this.previews = [...this.previewRail.querySelectorAll('.play-preview')];
      this.teasers = this.previews.map(preview => preview.querySelector('video'));
      this.previewVisibility = new Map();
      this.mobile = matchMedia('(max-width: 749px)');
      this.railLayout = this.dataset.feedLayout === 'rail';
      this.previewIndex = (this.railLayout || this.mobile.matches) ? 0 : Math.min(1,this.previews.length-1);
      this.teaserToggle = this.querySelector('[data-teaser-toggle]');
      this.sharePanel = this.querySelector('[data-reel-share-panel]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0; this.paused = this.motion.matches; this.teaserPaused = this.motion.matches;
      this.opening = false;
      const localeStrings = JSON.parse(document.getElementById('CatLocaleStrings')?.textContent || '{}');
  const localText = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.replaceAll('{'+name+'}', String(value)), localeStrings['client_'+key] || '');
      this.text = key => localText(key);
      this.setToggle = (button, playing, kind) => {
        if (!button) return;
        button.innerHTML = playing ? pauseIcon : playIcon;
        button.setAttribute('aria-label', this.text(kind + (playing ? '_pause' : '_play')));
        button.setAttribute('aria-pressed', String(playing));
      };
      this.teaserObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => this.previewVisibility.set(entry.target,entry.isIntersecting && entry.intersectionRatio >= .35));
        this.reconcile();
      }, { threshold: [0,.35] });
      this.previews.forEach(preview => this.teaserObserver.observe(preview));
      this.mobile.addEventListener('change', () => {
        this.activatePreview((this.railLayout || this.mobile.matches) ? this.nearestPreview() : Math.min(1,this.previews.length-1));
      }, {signal});
      this.previewRail.addEventListener('scroll', () => {
        if (this.railLayout && !this.mobile.matches && this.previewProgrammatic) return;
        if ((!this.railLayout && !this.mobile.matches) || this.previewFrame) return;
        this.previewFrame = requestAnimationFrame(() => {
          this.previewFrame = 0;
          const next = this.nearestPreview();
          if (next !== this.previewIndex) this.activatePreview(next);
        });
      }, {signal,passive:true});
      // Native touch scrolling keeps vertical page scroll and horizontal swipes independent.
      // Suppress a synthesized tap after a drag; ordinary taps still open the selected reel.
      this.previewRail.addEventListener('pointerdown', event => {
        this.previewProgrammatic = false;
        this.previewPointer = {x:event.clientX,y:event.clientY}; this.previewDragged = false;
      }, {signal,passive:true});
      this.previewRail.addEventListener('pointermove', event => {
        if (this.previewPointer && Math.hypot(event.clientX-this.previewPointer.x,event.clientY-this.previewPointer.y)>10) this.previewDragged = true;
      }, {signal,passive:true});
      this.previewRail.addEventListener('wheel', () => { this.previewProgrammatic = false; }, {signal,passive:true});
      this.previews.forEach((preview,index) => preview.addEventListener('pointerenter', event => {
        if (this.railLayout && !this.mobile.matches && event.pointerType === 'mouse') { this.previewProgrammatic = true; this.activatePreview(index); }
      }, {signal}));
      this.previewRail.addEventListener('pointercancel', () => { this.previewPointer = null; this.previewDragged = true; }, {signal});
      this.previewRail.addEventListener('pointerup', () => { this.previewPointer = null; }, {signal});
      this.previewRail.addEventListener('click', event => {
        if (this.previewDragged && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); this.previewDragged = false; }
      }, {signal,capture:true});
      this.modalObserver = new MutationObserver(() => this.reconcile());
      document.querySelectorAll('dialog').forEach(dialog => this.modalObserver.observe(dialog, { attributes:true, attributeFilter:['open'] }));
      document.addEventListener('visibilitychange', () => this.reconcile(), {signal});
      this.motion.addEventListener('change', () => {
        this.paused = this.motion.matches; this.teaserPaused = this.motion.matches; this.reconcile();
      }, {signal});
      document.addEventListener('click', event => {
        const link = event.target.closest('[data-play-channel]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); this.open(0,link);
      }, {signal});
      this.addEventListener('click', event => {
        const target = event.target.closest('button,a'); if (!target) return;
        if (target.hasAttribute('data-play-open')) {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault(); this.open(Number(target.dataset.playOpen), target);
        } else if (target.hasAttribute('data-play-close')) this.close();
        else if (target.hasAttribute('data-teaser-toggle')) { this.teaserPaused = !this.teasers[this.previewIndex].paused; this.reconcile(); }
        else if (target.hasAttribute('data-preview-next')) this.goPreview(this.previewIndex + 1);
        else if (target.hasAttribute('data-preview-prev')) this.goPreview(this.previewIndex - 1);
        else if (target.hasAttribute('data-reel-toggle')) { this.paused = !this.videos[this.index].paused; this.reconcile(); }
        else if (target.hasAttribute('data-reel-next')) this.go(this.index + 1);
        else if (target.hasAttribute('data-reel-prev')) this.go(this.index - 1);
        else if (target.hasAttribute('data-reel-share')) this.share(target);
        else if (target.hasAttribute('data-share-close')) this.closeShare();
        else if (target.hasAttribute('data-share-copy')) this.copyShare();
        else if (target.hasAttribute('data-reel-shop')) {
          const panel = this.slides[this.index].querySelector('[data-reel-shopping]');
          panel.classList.remove('is-highlighted');
          requestAnimationFrame(() => panel.classList.add('is-highlighted'));
          panel.querySelector('a')?.focus({preventScroll:true});
        }
      }, {signal});
      this.dialog.addEventListener('cancel', event => {
        if (event.target !== this.dialog) return;
        event.preventDefault(); this.sharePanel.hidden ? this.close() : this.closeShare();
      }, {signal});
      this.dialog.addEventListener('close', () => this.afterClose(), {signal});
      this.dialog.addEventListener('keydown', event => {
        if (document.querySelector('dialog[open]:not(.reels-dialog)') || !this.sharePanel.hidden || event.target.matches('input,select,textarea')) return;
        const next = ['ArrowDown','PageDown'].includes(event.key), previous = ['ArrowUp','PageUp'].includes(event.key);
        if (next || previous) { event.preventDefault(); this.go(this.index + (next ? 1 : -1)); }
        else if (event.key === ' ' && event.target === this.scroller) { event.preventDefault(); this.paused = !this.videos[this.index].paused; this.reconcile(); }
      }, {signal});
      this.scroller.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          if (this.opening || !this.dialog.open) return;
          const next = Math.max(0, Math.min(this.slides.length-1, Math.round(this.scroller.scrollTop / this.scroller.clientHeight)));
          if (next !== this.index) this.activate(next);
        });
      }, {signal, passive:true});
      this.resizeObserver = new ResizeObserver(() => {
        if (this.dialog.open && !this.opening) this.scroller.scrollTo({top:this.index*this.scroller.clientHeight,behavior:'instant'});
      });
      this.resizeObserver.observe(this.scroller);
      this.videos.forEach((video, index) => {
        const button = this.slides[index].querySelector('[data-reel-toggle]');
        video.addEventListener('playing', () => this.setToggle(button,true,'reel'), {signal});
        video.addEventListener('pause', () => this.setToggle(button,false,'reel'), {signal});
        video.addEventListener('timeupdate', () => {
          if (this.index === index && this.dialog.open) this.querySelector('[data-reel-progress]').style.transform = `scaleX(${video.duration ? video.currentTime/video.duration : 0})`;
        }, {signal});
        video.addEventListener('error', () => {
          if (this.dialog.open && this.index === index) this.querySelector('[data-reel-status]').textContent = this.text('video_error');
          this.setToggle(button,false,'reel');
        }, {signal});
      });
      this.teasers.forEach((video,index) => {
        video.addEventListener('playing', () => {
          if (!this.previewPermitted(index)) { video.pause(); return; }
          video.classList.add('is-playing'); this.setToggle(this.teaserToggle,true,'preview');
        }, {signal});
        video.addEventListener('pause', () => {
          if (index === this.previewIndex) this.setToggle(this.teaserToggle,false,'preview');
        }, {signal});
        video.addEventListener('error', () => {
          video.classList.remove('is-playing');
          if (index === this.previewIndex) this.setToggle(this.teaserToggle,false,'preview');
        }, {signal});
      });
      this.activatePreview(this.previewIndex);
      const slug = new URL(location.href).searchParams.get('reel');
      const initial = this.slides.findIndex(slide => slide.dataset.slug === slug);
      if (initial >= 0) this.open(initial, this.querySelector('[data-play-open]'));
    }
    async play(video, permitted) {
      video.autoplay = Boolean(permitted());
      if (!video.autoplay) { video.pause(); return; }
      video.muted = true; video.defaultMuted = true; video.playsInline = true;
      if (!video.hasAttribute('src')) { video.src = video.dataset.src; video.load(); }
      if (!video.paused || video.dataset.playPending) return;
      video.dataset.playPending = 'true';
      try { await video.play(); if (!permitted()) video.pause(); }
      catch (_) { /* Muted autoplay can still be denied. The visible control remains available. */ }
      finally { delete video.dataset.playPending; }
    }
    reconcile() {
      if (!this.isConnected) return;
      const permitted = () => this.dialog.open && !document.hidden && !this.paused && this.sharePanel.hidden && !document.querySelector('dialog[open]:not(.reels-dialog)');
      this.videos.forEach((video, index) => this.play(video, () => permitted() && index === this.index));
      this.teasers.forEach((video,index) => this.play(video, () => this.previewPermitted(index)));
    }
    previewPermitted(index) {
      return index === this.previewIndex && this.previewVisibility.get(this.previews[index]) && !this.teaserPaused && !document.hidden && !document.querySelector('dialog[open]');
    }
    nearestPreview() {
      const rail = this.previewRail.getBoundingClientRect(), inset = parseFloat(getComputedStyle(this.previewRail).paddingLeft) || 0, center = this.railLayout ? rail.left + inset : rail.left+rail.width/2;
      let nearest = 0, distance = Infinity;
      this.previews.forEach((preview,index) => {
        const rect = preview.getBoundingClientRect(), delta = Math.abs(rect.left+(this.railLayout ? 0 : rect.width/2)-center);
        if (rect.width && delta < distance) { nearest = index; distance = delta; }
      });
      return nearest;
    }
    activatePreview(index) {
      this.previewIndex = index;
      this.previews.forEach((preview,i) => {
        preview.classList.toggle('is-current',i===index);
        if (i===index) preview.setAttribute('aria-current','true'); else preview.removeAttribute('aria-current');
      });
      this.querySelector('[data-preview-count]').textContent = `${String(index+1).padStart(2,'0')} / ${String(this.previews.length).padStart(2,'0')}`;
      this.querySelector('[data-preview-prev]').disabled = index === 0;
      this.querySelector('[data-preview-next]').disabled = index === this.previews.length-1;
      this.querySelector('.play-enter').dataset.playOpen = (this.railLayout || this.mobile.matches) ? String(index) : '0';
      this.setToggle(this.teaserToggle,!this.teasers[index].paused,'preview');
      this.reconcile();
    }
    goPreview(index) {
      if (index < 0 || index >= this.previews.length) return;
      if (this.railLayout && !this.mobile.matches) { this.previewProgrammatic = true; this.activatePreview(index); }
      const rail = this.previewRail.getBoundingClientRect(), card = this.previews[index].getBoundingClientRect();
      this.previewRail.scrollTo({left:this.previewRail.scrollLeft+card.left-rail.left+(this.railLayout ? -(parseFloat(getComputedStyle(this.previewRail).paddingLeft)||0) : card.width/2-rail.width/2),behavior:this.motion.matches?'instant':'smooth'});
    }
    open(index, trigger) {
      this.origin = trigger?.closest('#MenuDialog') ? document.querySelector('[data-open="MenuDialog"]') : trigger;
      this.opening = true;
      document.querySelectorAll('dialog[open]').forEach(dialog => { if (dialog !== this.dialog) dialog.close(); });
      if (!this.dialog.open) this.dialog.showModal();
      this.paused = this.motion.matches;
      this.activate(index);
      this.scroller.scrollTo({top:this.index*this.scroller.clientHeight,behavior:'instant'});
      requestAnimationFrame(() => { this.opening = false; });
      this.querySelector('[data-play-close]').focus({preventScroll:true});
      this.reconcile();
    }
    activate(index) {
      const focused = document.activeElement;
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.slides.forEach((slide,i) => { slide.inert = i !== this.index; slide.classList.toggle('is-active',i===this.index); });
      this.querySelector('[data-reel-count]').textContent = `${String(this.index+1).padStart(2,'0')} / ${String(this.slides.length).padStart(2,'0')}`;
      this.querySelector('[data-reel-prev]').disabled = this.index === 0;
      this.querySelector('[data-reel-next]').disabled = this.index === this.slides.length-1;
      if (this.dialog.open && (this.slides.some((slide,i) => i !== this.index && slide.contains(focused)) || (this.dialog.contains(focused) && focused.disabled))) this.scroller.focus({preventScroll:true});
      this.querySelector('[data-reel-progress]').style.transform = 'scaleX(0)';
      this.querySelector('[data-reel-status]').textContent = this.slides[this.index].querySelector('h3').textContent;
      if (this.dialog.open) { const url = new URL(location.href); url.searchParams.set('reel',this.slides[this.index].dataset.slug); history.replaceState(history.state,'',url); }
      this.reconcile();
    }
    go(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.scroller.scrollTo({top:index*this.scroller.clientHeight,behavior:this.motion.matches?'instant':'smooth'});
    }
    close() { this.closeShare(false); this.dialog.close(); }
    afterClose() {
      this.videos.forEach(video => video.pause());
      this.closeShare(false);
      const url = new URL(location.href); url.searchParams.delete('reel'); history.replaceState(history.state,'',url);
      if (this.origin?.isConnected && !document.querySelector('dialog[open]')) this.origin.focus({preventScroll:true});
      this.reconcile();
    }
    shareUrl() {
      const url = new URL(document.body.dataset.root || '/',location.origin);
      url.searchParams.set('reel',this.slides[this.index].dataset.slug);
      const currentPreview = new URL(location.href).searchParams.get('preview_theme_id');
      const theme = window.Shopify?.theme;
      const preview = currentPreview || (theme?.role === 'unpublished' ? String(theme.id) : '');
      if (/^\d+$/.test(preview)) url.searchParams.set('preview_theme_id',preview);
      return url.href;
    }
    async share(trigger) {
      const url = this.shareUrl(), title = 'Spotted', text = this.slides[this.index].querySelector('h3').textContent;
      if (navigator.share) {
        try { await navigator.share({title,text,url}); return; }
        catch (error) { if (error.name === 'AbortError') return; }
      }
      this.shareOrigin = trigger;
      this.sharePanel.hidden = false;
      this.scroller.inert = true;
      this.querySelector('.reels-topbar').inert = true;
      this.querySelector('.reels-navigation').inert = true;
      this.querySelector('[data-share-url]').value = url;
      this.querySelector('[data-share-status]').textContent = '';
      this.querySelector('[data-share-close]').focus({preventScroll:true});
      this.reconcile();
    }
    closeShare(focus = true) {
      if (this.sharePanel.hidden) return;
      this.sharePanel.hidden = true; this.scroller.inert = false;
      this.querySelector('.reels-topbar').inert = false;
      this.querySelector('.reels-navigation').inert = false;
      if (focus) this.shareOrigin?.focus({preventScroll:true});
      this.reconcile();
    }
    async copyShare() {
      const input = this.querySelector('[data-share-url]'), status = this.querySelector('[data-share-status]');
      try { await navigator.clipboard.writeText(input.value); status.textContent = this.text('link_copied'); }
      catch (_) { input.focus(); input.select(); status.textContent = this.text('link_manual'); }
    }
    disconnectedCallback() {
      this.abort?.abort(); this.teaserObserver?.disconnect(); this.modalObserver?.disconnect(); this.resizeObserver?.disconnect();
      [...this.videos || [],...this.teasers || []].forEach(video=> { video.autoplay = false; video.pause(); });
      cancelAnimationFrame(this.scrollFrame); cancelAnimationFrame(this.previewFrame);
      this.ready = false;
    }
  }
  customElements.define('catharsis-play',CatharsisPlay);
})();
